import React, { useState } from 'react';
import {
  TableExpandRow,
  TableCell,
  TableExpandedRow,
  TooltipDefinition
} from 'carbon-components-react';
import { NoImage16, Checkmark16, Information20 } from '@carbon/icons-react';
import { getJobStatusCodeMsg } from 'appConstants/enquiry';
import { JOB_STATUS_CODE } from 'types';
import { ReactComponent as WaterLogo } from 'assets/icons/ui-icon-utility-16-water-circle.svg';
import { ReactComponent as ElectricityLogo } from 'assets/icons/ui-icon-utility-16-electricity-circle.svg';
import { ReactComponent as GasLogo } from 'assets/icons/ui-icon-utility-16-gas-circle.svg';
import { ReactComponent as GovLogo } from 'assets/icons/ui-icon-utility-16-gov-circle.svg';
import { ReactComponent as OtherLogo } from 'assets/icons/ui-icon-utility-16-other-circle.svg';

interface Props {
  row: any;
  getRowProps: Function;
  index: number;
  headers: any[];
  handleResponseClick?: Function;
  showResponsePreview?: boolean;
}

const getIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'water': {
      return <WaterLogo className="utilityIcon" />;
    }
    case 'electric': {
      return <ElectricityLogo className="utilityIcon" />;
    }
    case 'gas': {
      return <GasLogo className="utilityIcon" />;
    }
    case 'local government': {
      return <GovLogo className="utilityIcon" />;
    }
    case 'telco': {
      const iconName = 'ui-icon-utility-16-comms-circle';
      return (
        <img
          src={`../${iconName}.png`}
          srcSet={`../${iconName}.png, ../${iconName}@2x.png 2x, ../${iconName}@3x.png 3x`}
          width="16"
          height="16"
          alt=""
          className="utilityIcon"
        />
      );
    }
    case 'other': {
      return <OtherLogo className="utilityIcon" />;
    }
    default:
      return undefined;
  }
};

const getTooltipText = (type: string) => {
  switch (type.toLowerCase()) {
    case 'water': {
      return 'Water';
    }
    case 'electric': {
      return 'Electricity';
    }
    case 'gas': {
      return 'Gas';
    }
    case 'local government': {
      return 'Government';
    }
    case 'telco': {
      return 'Telecommunications';
    }
    case 'other': {
      return 'Other';
    }
    default:
      return undefined;
  }
};

const ReferralsDataTableRow: React.FC<Props> = ({
  row,
  getRowProps,
  index,
  headers,
  handleResponseClick,
  showResponsePreview
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const cells = row.cells;
  const c_0 = cells[0],
    c_1 = cells[1],
    c_2 = cells[2],
    c_3 = cells[3],
    c_4 = cells[4],
    c_5 = cells[5],
    c_6 = cells[6],
    c_7 = cells[7];
  const showMemberAnnouncement =
    c_7?.value?.length > 0 && c_5?.value !== JOB_STATUS_CODE.COMPLETED;

  const totalResponse = c_6?.value?.length || 0;

  return (
    <>
      <TableExpandRow
        {...getRowProps({ row })}
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        className={showMemberAnnouncement ? 'has-announcement' : ''}
      >
        <TableCell key={cells[index]?.info?.header}>
          <div className="row--header">
            <span key={'c_1'} className="row--header-title">
              {c_1?.value}
            </span>
            <div className="row--header-logo">
              {c_5?.value === JOB_STATUS_CODE.COMPLETED && (
                <Checkmark16 className="checkmark" />
              )}
              {showMemberAnnouncement && (
                <span className="announcement-icon">
                  <Information20 />
                </span>
              )}
              <span key={'c_0'} title={getTooltipText(c_0?.value)}>
                {c_0?.value !== undefined ? getIcon(c_0?.value) : <NoImage16 />}
              </span>
            </div>
          </div>
          <div className="row--sub-header" key={'c_2'}>
            <p>Referral ID {c_2?.value}</p>
            <p>Typical wait {c_4?.value || 'Not available'}</p>
          </div>
        </TableCell>
      </TableExpandRow>
      <TableExpandedRow
        colSpan={headers.length + 1}
        className={`row-expanded--wrapper ${
          showMemberAnnouncement ? 'has-announcement' : ''
        }`}
      >
        <div className="row-expanded--body">
          <div key={'c_5'}>
            Status{' '}
            <span
              className={`value-text${
                c_5?.value === JOB_STATUS_CODE.COMPLETED ? '-green' : ''
              }`}
            >
              {getJobStatusCodeMsg(c_5?.value)}
            </span>
          </div>
          {c_3?.value.dom ? (
            c_3?.value.dom
          ) : (
            <React.Fragment>
              <div key={'c_3'}>
                Phone <span className="value-text">{c_3?.value.phone}</span>
              </div>
              <div key={`${'c_3'}_email`}>
                Email <span className="value-text">{c_3?.value.email}</span>
              </div>
            </React.Fragment>
          )}
          {showResponsePreview && c_6?.value?.length > 0 ? (
            <div className="preview-section">
              <div className="preview-title">Preview</div>
              <div className="preview-list">
                {c_6.value.map((res: any, idx: number) => {
                  const currentCountNo = totalResponse - idx;
                  return (
                    <>
                      {!res.expired && (
                        <div
                          key={idx}
                          onClick={() =>
                            handleResponseClick &&
                            handleResponseClick(
                              res.outboundEmailId,
                              res.inboundEmailId
                            )
                          }
                        >
                          Response-{currentCountNo}
                        </div>
                      )}

                      {res.expired && (
                        <TooltipDefinition
                          tooltipText="Response content is only retained for 90 days"
                          direction="top"
                          align="center"
                          className="response-expired-tooltip"
                        >
                          <span>Response-{currentCountNo}</span>
                        </TooltipDefinition>
                      )}
                    </>
                  );
                })}
              </div>
            </div>
          ) : (
            ''
          )}
        </div>
      </TableExpandedRow>
      {showMemberAnnouncement && (
        <tr>
          <td colSpan={2} className="has-announcement-td-container">
            <div className="member-announcement">
              <div className="member-announcement-header">
                MEMBER ANNOUNCEMENT
              </div>
              <div className="member-announcement-wrapper">
                {c_7?.value.map((announcement: any) => {
                  return (
                    <>
                      <div className="member-announcement-title">
                        {announcement.title}
                      </div>
                      <div
                        className="member-announcement-body"
                        dangerouslySetInnerHTML={{ __html: announcement.text }}
                      />
                    </>
                  );
                })}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default React.memo(ReferralsDataTableRow);
